import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import { SITE, HYROX_NAV_SUB } from "../data/mock";
import Logo from "./Logo";

function DropItem({ to, title, sub, indent = false, accent = false }) {
  return (
    <Link to={to} className={`group flex items-start gap-3 p-3 hover:bg-white/5 transition-colors ${indent ? "pl-5" : ""}`}>
      <span className="mt-1.5 w-1.5 h-1.5 bg-[#9D0423] shrink-0 group-hover:scale-125 transition-transform" />
      <span>
        <span className={`block text-[13px] font-semibold ${accent ? "text-[#E56A79]" : "text-neutral-200"} group-hover:text-white`}>{title}</span>
        <span className="block text-[11px] text-neutral-500 leading-snug mt-0.5">{sub}</span>
      </span>
    </Link>
  );
}

const navLinks = [
  { label: "Programs", to: "/classes", dropdown: true },
  { label: "HYROX", to: "/hyrox" },
  { label: "Live B.I.G. 365", to: "/live-big-365" },
  { label: "Schedule", to: "/schedule" },
  { label: "Pricing", to: "/pricing" },
  { label: "About", to: "/about" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [progOpen, setProgOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setProgOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* Top utility bar */}
      <div className="hidden md:block bg-[#0A0A0B] border-b border-white/5 relative z-[60]">
        <div className="max-w-7xl mx-auto px-6 h-9 flex items-center justify-between">
          <a href={SITE.phoneHref} className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.14em] text-neutral-400 hover:text-white transition-colors">
            <Phone size={11} className="text-[#9D0423]" /> {SITE.phone}
          </a>
          <div className="flex items-center gap-6 text-[11px] font-mono uppercase tracking-[0.14em]">
            <span className="text-neutral-500">{SITE.address.full}</span>
            <Link to="/drop-in" className="text-neutral-400 hover:text-white transition-colors">Visiting Athlete →</Link>
          </div>
        </div>
      </div>

      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-[#0A0A0B]/95 backdrop-blur-md border-b border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.5)]" : "bg-[#0A0A0B] border-b border-white/5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 lg:h-[72px]">
            {/* Logo */}
            <Link to="/" className="group shrink-0" aria-label="Boston Iron Grit home">
              <Logo />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) =>
                link.dropdown ? (
                  <div
                    key={link.to}
                    className="relative"
                    onMouseEnter={() => setProgOpen(true)}
                    onMouseLeave={() => setProgOpen(false)}
                  >
                    <NavLink
                      to={link.to}
                      className={({ isActive }) =>
                        `flex items-center gap-1 px-2 py-2 text-[12px] font-mono uppercase tracking-[0.08em] whitespace-nowrap transition-colors ${
                          isActive ? "text-white" : "text-neutral-400 hover:text-white"
                        }`
                      }
                    >
                      {link.label} <ChevronDown size={13} className={`transition-transform duration-200 ${progOpen ? "rotate-180" : ""}`} />
                    </NavLink>
                    {progOpen && (
                      <div className="absolute left-0 top-full pt-2 w-[560px] z-[70]">
                        <div className="bg-[#121214] border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.7)] p-4 grid grid-cols-2 gap-x-4">
                          {/* Column 1: training programs */}
                          <div>
                            <DropItem to="/classes/crossfit" title="CrossFit" sub="Group classes · Mon–Fri" />
                            <div className="px-3 pt-3 pb-1 font-mono text-[9px] uppercase tracking-[0.26em] text-neutral-600">Westie Barbell Club</div>
                            <DropItem to="/classes/olympic-weightlifting" title="Olympic Lifting" sub="Saturday mornings" indent />
                            <DropItem to="/classes/powerlifting" title="Powerlifting" sub="Tue/Thu AM · Wed/Fri AM" indent />
                            <DropItem to="/classes/big-engine" title="B.I.G. Engine" sub="Sunday engine builder" />
                            <DropItem to="/classes/fundamentals" title="Beginner Fundamentals" sub="Start here" />
                          </div>
                          {/* Column 2: HYROX, open gym, Live B.I.G. */}
                          <div>
                            <DropItem to="/hyrox" title="HYROX" sub={HYROX_NAV_SUB} accent />
                            <DropItem to="/classes/open-gym" title="Open Gym" sub="Mon–Fri 10:30–11:30 AM" />
                            <div className="px-3 pt-3 pb-1 font-mono text-[9px] uppercase tracking-[0.26em] text-neutral-600">Live B.I.G. 365</div>
                            <DropItem to="/live-big-365" title="Program Overview" sub="Wellness & longevity" indent accent />
                            <DropItem to="/live-big-365#habits" title="8 Daily Habits" sub="Train · Nourish · Connect" indent />
                            <DropItem to="/peptides-glp1-boston" title="Peptides & GLP-1s" sub="Provider-guided programs" indent accent />
                            <DropItem to="/classes/nutrition-coaching" title="Nutrition Coaching" sub="Habit-based coaching" indent />
                            <DropItem to="/classes/inbody-testing" title="Body Composition Test" sub="Know your baseline" indent />
                            <Link to="/gold-standard" className="group flex items-start gap-3 p-3 hover:bg-white/5 transition-colors">
                              <span className="mt-1.5 w-1.5 h-1.5 bg-[#D4AF37] shrink-0 group-hover:scale-125 transition-transform" />
                              <span>
                                <span className="block text-[13px] font-semibold text-[#E8C766] group-hover:text-white">Gold Standard Programs</span>
                                <span className="block text-[11px] text-neutral-500 leading-snug mt-0.5">Performance · Longevity · Avidity</span>
                              </span>
                            </Link>
                            <Link to="/classes" className="mt-2 flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 transition-colors">
                              <span className="text-[12px] font-mono uppercase tracking-[0.12em] text-neutral-300">All Programs</span>
                              <ArrowRight size={14} className="text-[#9D0423]" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    className={({ isActive }) =>
                      `px-2 py-2 text-[12px] font-mono uppercase tracking-[0.08em] whitespace-nowrap transition-colors ${
                        isActive ? "text-white" : "text-neutral-400 hover:text-white"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                )
              )}
            </nav>

            {/* CTA + mobile toggle */}
            <div className="flex items-center gap-3">
              <Link
                to="/get-started"
                className="hidden sm:inline-flex items-center gap-2 bg-[#9D0423] hover:bg-[#7E031C] text-white text-[12px] font-mono font-semibold uppercase tracking-[0.12em] whitespace-nowrap px-4 py-3 transition-all duration-200 hover:translate-y-[-1px]"
              >
                Free Goal Review <ArrowRight size={14} />
              </Link>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="lg:hidden p-2 text-neutral-300 hover:text-white transition-colors"
                aria-label="Toggle menu"
              >
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-[#0A0A0B] border-t border-white/10 max-h-[calc(100vh-64px)] overflow-y-auto">
            <nav className="px-6 py-6 flex flex-col gap-1">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    `py-3 border-b border-white/5 text-sm font-mono uppercase tracking-[0.14em] ${
                      isActive ? "text-[#9D0423]" : "text-neutral-300"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <Link to="/peptides-glp1-boston" className="py-3 border-b border-white/5 text-sm font-mono uppercase tracking-[0.14em] text-neutral-300">
                Peptides & GLP-1s
              </Link>
              <Link to="/gold-standard" className="py-3 border-b border-white/5 text-sm font-mono uppercase tracking-[0.14em] text-[#E8C766]">
                Gold Standard Programs
              </Link>
              <Link to="/drop-in" className="py-3 border-b border-white/5 text-sm font-mono uppercase tracking-[0.14em] text-neutral-300">
                Drop In
              </Link>
              <Link
                to="/get-started"
                className="mt-4 inline-flex items-center justify-center gap-2 bg-[#9D0423] text-white text-sm font-mono font-semibold uppercase tracking-[0.14em] px-5 py-4"
              >
                Free Goal Review <ArrowRight size={15} />
              </Link>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
